import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { useAuth } from "@/context/AuthContext";
import api, { fileUrl } from "@/lib/api";
import { getGarment } from "@/data/garments";
import { STATUS_LABELS } from "@/data/credits";
import { Loader2, Users, Package, Coins, CreditCard, ChevronDown, ChevronRight, ShieldCheck } from "lucide-react";

const STATUS_STYLES = {
  waiting: "bg-neutral-100 text-neutral-600",
  production: "bg-blue-50 text-[#002FA7]",
  done: "bg-green-50 text-green-700",
};

const Stat = ({ icon: Icon, label, value }) => (
  <div className="border border-border rounded-sm p-5 bg-white flex items-center gap-4" data-testid={`stat-${label}`}>
    <div className="w-11 h-11 rounded-sm bg-[#002FA7]/10 text-[#002FA7] flex items-center justify-center"><Icon className="w-5 h-5" /></div>
    <div>
      <div className="text-2xl font-display font-semibold tabular-nums leading-none">{value}</div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mt-1.5">{label}</div>
    </div>
  </div>
);

export default function Admin() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(true);
  const [open, setOpen] = useState({});
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/");
  }, [loading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const [s, u] = await Promise.all([api.get("/admin/stats"), api.get("/admin/users")]);
        setStats(s.data);
        setUsers(u.data);
      } catch (e) {
        if (e?.response?.status === 403) setDenied(true);
      }
      setBusy(false);
    })();
  }, [user]);

  if (denied) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <div className="flex-1 flex flex-col items-center justify-center gap-3 text-center px-6">
          <ShieldCheck className="w-10 h-10 text-neutral-300" />
          <h1 className="font-display text-2xl tracking-tight font-semibold">Acceso restringido</h1>
          <p className="text-neutral-500">Este panel es solo para administradores.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <Header />
      <div className="max-w-6xl w-full mx-auto px-6 py-12 flex-1">
        <div className="mb-8 border-b border-border pb-6">
          <p className="text-xs uppercase tracking-[0.25em] text-neutral-500 mb-2">Administración</p>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight font-semibold">Panel de control</h1>
        </div>

        {busy ? (
          <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-[#002FA7]" /></div>
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="admin-stats">
              <Stat icon={Users} label="Usuarios" value={stats?.total_users ?? 0} />
              <Stat icon={Package} label="Mockups" value={stats?.total_mockups ?? 0} />
              <Stat icon={Coins} label="Créditos gastados" value={stats?.credits_consumed ?? 0} />
              <Stat icon={CreditCard} label="Compras" value={stats?.paid_orders ?? 0} />
            </div>

            <div className="flex gap-3 mb-8 text-xs">
              {["waiting", "production", "done"].map((s) => (
                <span key={s} className={`px-3 py-1.5 rounded-sm ${STATUS_STYLES[s]}`}>
                  {STATUS_LABELS[s]}: <strong>{stats?.production?.[s] ?? 0}</strong>
                </span>
              ))}
            </div>

            <p className="text-xs uppercase tracking-[0.2em] text-neutral-500 mb-4">Usuarios registrados</p>
            <div className="border border-border rounded-sm overflow-hidden" data-testid="admin-users">
              <div className="grid grid-cols-12 px-4 py-2.5 bg-neutral-50 text-[10px] uppercase tracking-[0.15em] text-neutral-500 border-b border-border">
                <div className="col-span-5">Usuario</div>
                <div className="col-span-2 text-right">Créditos</div>
                <div className="col-span-2 text-right">Gastados</div>
                <div className="col-span-3 text-right">Diseños</div>
              </div>
              {users.map((u) => (
                <div key={u.user_id} className="border-b border-border/60 last:border-0" data-testid={`admin-user-${u.user_id}`}>
                  <button onClick={() => setOpen((o) => ({ ...o, [u.user_id]: !o[u.user_id] }))} className="w-full grid grid-cols-12 px-4 py-3 items-center hover:bg-neutral-50 text-left">
                    <div className="col-span-5 flex items-center gap-2 min-w-0">
                      {open[u.user_id] ? <ChevronDown className="w-4 h-4 text-neutral-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-neutral-400 shrink-0" />}
                      {u.picture ? <img src={u.picture} alt="" className="w-7 h-7 rounded-full border border-border" /> : <div className="w-7 h-7 rounded-full bg-neutral-200 flex items-center justify-center text-[11px]">{(u.email || "?")[0].toUpperCase()}</div>}
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate flex items-center gap-1.5">{u.name || u.email} {u.is_admin && <ShieldCheck className="w-3.5 h-3.5 text-[#002FA7]" />}</div>
                        <div className="text-xs text-neutral-400 truncate">{u.email}</div>
                      </div>
                    </div>
                    <div className="col-span-2 text-right tabular-nums font-semibold text-[#CA8A04]">{u.credits}</div>
                    <div className="col-span-2 text-right tabular-nums text-neutral-600">{u.consumed}</div>
                    <div className="col-span-3 text-right text-sm">
                      <span className="tabular-nums font-medium">{u.designs_count}</span>
                      <span className="text-neutral-400"> · {u.status_counts.production} en prod.</span>
                    </div>
                  </button>
                  {open[u.user_id] && (
                    <div className="px-4 pb-4 pt-1 bg-neutral-50/50">
                      {u.designs.length === 0 ? (
                        <p className="text-xs text-neutral-400 py-2">Sin diseños.</p>
                      ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
                          {u.designs.map((m) => (
                            <div key={m.id} className="border border-border rounded-sm overflow-hidden bg-white" data-testid={`admin-design-${m.id}`}>
                              <div className="aspect-square bg-[#F4F4F5] flex items-center justify-center">
                                <img src={fileUrl(m.preview_path)} alt={m.name} className="w-full h-full object-contain" />
                              </div>
                              <div className="p-2 border-t border-border">
                                <div className="text-[11px] font-medium truncate">{m.name}</div>
                                <div className="flex items-center justify-between mt-1">
                                  <span className="text-[10px] text-neutral-400">{getGarment(m.garment).label}</span>
                                  <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${STATUS_STYLES[m.spec?.status || "waiting"]}`}>{STATUS_LABELS[m.spec?.status || "waiting"]}</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
